/**
 * Restores the overall page/wizard scroll position to the top and moves
 * keyboard focus to the step heading for accessibility.
 *
 * Used by multi-step wizards (e.g. Create Job) so every step change —
 * Next, Back, step jump, draft restore or validation redirect — starts
 * the user at the top of the new step.
 *
 * Behaviour:
 * - Smooth scrolling where supported, instant ("auto") fallback otherwise.
 * - Respects prefers-reduced-motion by scrolling instantly.
 * - Scrolls the window AND any scrollable ancestor container (native shell
 *   scrolls a container, web scrolls the window).
 * - Focus uses preventScroll so it never fights the scroll animation.
 * - Never touches entered form data or per-component scroll state.
 */

const supportsSmoothScroll = (): boolean =>
  typeof document !== "undefined" &&
  "scrollBehavior" in document.documentElement.style;

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export interface ScrollWizardToTopOptions {
  /** Wizard root element; its scrollable ancestors are also reset. */
  root?: HTMLElement | null;
  /** Heading (or first field) to receive keyboard focus after scrolling. */
  focusTarget?: HTMLElement | null;
}

const scrollElementToTop = (el: HTMLElement, behavior: ScrollBehavior) => {
  try {
    el.scrollTo({ top: 0, left: 0, behavior });
  } catch {
    // jsdom / very old WebViews: fall back to direct assignment.
    el.scrollTop = 0;
    el.scrollLeft = 0;
  }
};

/**
 * Selectors for app-level scroll containers. The native shell and several
 * layouts scroll an element rather than the window, so a window-only reset
 * leaves the new screen/step halfway down.
 */
const SCROLL_CONTAINER_SELECTORS = [
  "[data-scroll-container]",
  "main",
  "[data-radix-scroll-area-viewport]",
].join(",");

/** Reset every app-level scroll container to the top. */
export const scrollContainersToTop = (behavior: ScrollBehavior = "auto") => {
  if (typeof document === "undefined") return;
  document.querySelectorAll<HTMLElement>(SCROLL_CONTAINER_SELECTORS).forEach((el) => {
    if (el.scrollTop > 0) scrollElementToTop(el, behavior);
  });
};

export const scrollWizardToTop = (options: ScrollWizardToTopOptions = {}) => {
  const behavior: ScrollBehavior =
    supportsSmoothScroll() && !prefersReducedMotion() ? "smooth" : "auto";

  // 1) Window-level scroll (standard web layout). Direct assignment as well —
  //    iOS WKWebView can silently ignore smooth window.scrollTo during a step
  //    transition, which left the new step part-way down the page.
  try {
    window.scrollTo({ top: 0, left: 0, behavior });
  } catch {
    window.scrollTo(0, 0);
  }
  if (typeof document !== "undefined") {
    if (document.documentElement.scrollTop > 0) document.documentElement.scrollTop = 0;
    if (document.body.scrollTop > 0) document.body.scrollTop = 0;
  }

  // 2) Scrollable ancestor containers (e.g. native shell <main>).
  let el = options.root?.parentElement ?? null;
  while (el) {
    if (el.scrollHeight > el.clientHeight) {
      const overflowY = window.getComputedStyle(el).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        scrollElementToTop(el, behavior);
      }
    }
    el = el.parentElement;
  }

  // 2b) App-level scroll containers (native shell), even when no root is given.
  scrollContainersToTop(behavior);


  // 3) Move keyboard/screen-reader focus to the step heading without
  //    triggering a competing scroll.
  const focusTarget = options.focusTarget;
  if (focusTarget) {
    requestAnimationFrame(() => {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch {
        focusTarget.focus();
      }
    });
  }
};
